/** Phone-landscape layout detection.
 *
 * The desktop UI is authored for 1366x768 and 1920x1080. A phone in landscape
 * is roughly 800x390 CSS pixels - half the height, a different input model.
 * This module is the single source of truth for "are we in that viewport".
 *
 * Width and height are CSS pixels of the window (innerWidth / innerHeight),
 * not device pixels: a 3x phone at 390 CSS rows is still a 390-row layout.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties, RefObject } from "react";

/** Short side at or below this (CSS px) is a phone, not a laptop. */
export const PHONE_SHORT_MAX = 520;
/** Long side at or below this, so an iPad landscape stays on the desktop UI. */
export const PHONE_LONG_MAX = 1100;

export function isPhoneViewport(width: number, height: number): boolean {
  if (!(width > 0) || !(height > 0)) return false;
  return Math.min(width, height) <= PHONE_SHORT_MAX
    && Math.max(width, height) <= PHONE_LONG_MAX;
}

export function isPhoneLandscape(width: number, height: number): boolean {
  return isPhoneViewport(width, height) && width >= height;
}

export function isPhonePortrait(width: number, height: number): boolean {
  return isPhoneViewport(width, height) && height > width;
}

export interface PhoneLayout {
  readonly landscape: boolean;
  readonly portrait: boolean;
  /** Either orientation of a phone viewport. */
  readonly phone: boolean;
}

const DESKTOP: PhoneLayout = { landscape: false, portrait: false, phone: false };

function readPhoneLayout(): PhoneLayout {
  if (typeof window === "undefined") return DESKTOP;
  const w = window.innerWidth;
  const h = window.innerHeight;
  return {
    landscape: isPhoneLandscape(w, h),
    portrait: isPhonePortrait(w, h),
    phone: isPhoneViewport(w, h),
  };
}

/** Live viewport class: landscape phone, portrait phone, or desktop. */
export function usePhoneLayout(): PhoneLayout {
  const [state, setState] = useState<PhoneLayout>(readPhoneLayout);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const apply = (): void => {
      const next = readPhoneLayout();
      setState((prev) => (
        prev.landscape === next.landscape && prev.portrait === next.portrait
          && prev.phone === next.phone ? prev : next
      ));
    };
    apply();
    window.addEventListener("resize", apply);
    window.addEventListener("orientationchange", apply);
    return () => {
      window.removeEventListener("resize", apply);
      window.removeEventListener("orientationchange", apply);
    };
  }, []);
  return state;
}

/**
 * How far to shrink a desktop-authored plate so it fits a landscape-phone
 * window. Pure, so a test can pin "the whole sheet is on screen".
 */
export function phoneSheetScale(
  availW: number, availH: number, sheetW: number, sheetH: number,
  fit: "both" | "width" = "both",
): number {
  if (!(availW > 0) || !(availH > 0) || !(sheetW > 0) || !(sheetH > 0)) return 1;
  // `width` is the portrait answer: a sheet shrunk until its HEIGHT fits a
  // 844-row phone is a sheet nobody can read or tap, so the width is fitted and
  // the rest is scrolled. `both` is the landscape answer, where the window is
  // the shape the plate was drawn for and the whole thing can be on screen.
  return fit === "width"
    ? Math.min(1, availW / sheetW)
    : Math.min(1, availW / sheetW, availH / sheetH);
}

/**
 * How much layout height to take back once a sheet has been scaled.
 *
 * `transform: scale()` does not change the box the scroller measures: a 1,200px
 * plate drawn at 0.32 still reserves 1,200px, so the scroll bar runs three
 * times past the bottom of the art and the player scrolls into nothing. The
 * negative margin is exactly the difference, which makes the scrollable area
 * the height of what is actually drawn.
 */
export function phoneSheetTrim(sheetH: number, scale: number): number {
  if (!(sheetH > 0) || !(scale > 0) || scale >= 1) return 0;
  return Math.round(sheetH * (1 - scale));
}

/** CSS px to shift a scaled plate so it sits in the middle of the window.
 *  Origin is top-left; a wide book would otherwise hang off the right edge. */
export function phoneSheetShiftX(
  availW: number, sheetW: number, scale: number, sheetLeft = 0,
): number {
  if (!(availW > 0) || !(sheetW > 0) || !(scale > 0)) return 0;
  return Math.round((availW - sheetW * scale) / 2 - sheetLeft);
}

function offsetLeftIn(el: HTMLElement, ancestor: HTMLElement): number {
  let x = 0;
  let node: HTMLElement | null = el;
  while (node !== null && node !== ancestor) {
    x += node.offsetLeft;
    node = node.offsetParent instanceof HTMLElement ? node.offsetParent : null;
  }
  return x;
}

export function attachRefs<T>(
  node: T | null, ...refs: readonly (RefObject<T> | undefined)[]
): void {
  for (const r of refs) {
    if (r !== undefined) (r as { current: T | null }).current = node;
  }
}

/**
 * Scale a screen's carved plate to a landscape-phone window the way the hero
 * sheet does: measure the plate, set `--hs-phone-scale`, and let CSS transform
 * it. Put `screenRef` on the `.hs-screen` and `sheetRef` on `.hs-phone-sheet`
 * (the bar plus the body).
 */
export function usePhoneSheetFit(): {
  readonly phone: PhoneLayout;
  readonly scale: number;
  readonly screenRef: RefObject<HTMLDivElement>;
  readonly sheetRef: RefObject<HTMLDivElement>;
  readonly screenClass: string;
  readonly style: CSSProperties;
} {
  const phone = usePhoneLayout();
  const portrait = phone.portrait;
  const screenRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState({ scale: 0.5, shiftX: 0, trim: 0 });

  useLayoutEffect(() => {
    if (!phone.phone) {
      setFit({ scale: 1, shiftX: 0, trim: 0 });
      return undefined;
    }
    const screen = screenRef.current;
    const sheet = sheetRef.current;
    if (screen === null || sheet === null) return undefined;
    const apply = (): void => {
      const pad = 8;
      const availW = screen.clientWidth - pad;
      const availH = screen.clientHeight - pad;
      /**
       * The sheet's CONTENT, not its box.
       *
       * The plate is a flex child and its box is whatever the phone window
       * gives it; the desktop-authored grid inside overflows that box and is
       * drawn outside it (`overflow: visible` is what stops the columns being
       * cut). Measured as a box, a 1,150px plate in a 382px window reported
       * 382 and the fit said "scale 1, it fits" - and the player got the left
       * third of his hero sheet with no way to reach the rest. `scrollWidth`
       * and `scrollHeight` are the content, which is the thing being fitted.
       */
      const sheetW = Math.max(sheet.scrollWidth, sheet.offsetWidth);
      const sheetH = Math.max(sheet.scrollHeight, sheet.offsetHeight);
      const next = phoneSheetScale(availW, availH, sheetW, sheetH, portrait ? "width" : "both");
      const shiftX = phoneSheetShiftX(
        screen.clientWidth, sheetW, next, offsetLeftIn(sheet, screen),
      );
      const trim = phoneSheetTrim(sheetH, next);
      setFit((prev) => (
        Math.abs(prev.scale - next) < 0.008 && prev.shiftX === shiftX && prev.trim === trim
          ? prev
          : { scale: next, shiftX, trim }
      ));
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(screen);
    ro.observe(sheet);
    return () => { ro.disconnect(); };
  }, [phone.phone, portrait]);

  return {
    phone,
    scale: phone.phone ? fit.scale : 1,
    screenRef,
    sheetRef,
    screenClass: phone.phone ? "hs-screen--phone-fit" : "",
    style: phone.phone
      ? {
          "--hs-phone-scale": String(fit.scale),
          "--hs-phone-shift-x": `${String(fit.shiftX)}px`,
          "--hs-phone-trim": `${String(fit.trim)}px`,
        } as CSSProperties
      : {},
  };
}
